import React, { useState, useEffect } from 'react';
import {
  ExclamationTriangleIcon,
  BellIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { nutritionApi } from '../api/nutrition';

interface Alert {
  type: 'deficiency' | 'excess' | 'imbalance' | 'quality' | 'cost';
  nutrient: string;
  severity: 'low' | 'medium' | 'high';
  message: string;
  location?: string;
  date: string;
  value?: number;
  threshold?: number;
  recommendation?: string;
}

interface NutritionalAlertsPanelProps {
  selectedLocation?: string;
  onAlertAction?: (alertId: string, action: 'acknowledge' | 'resolve') => void;
}

export default function NutritionalAlertsPanel({ 
  selectedLocation,
  onAlertAction 
}: NutritionalAlertsPanelProps) {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [showResolved, setShowResolved] = useState(false);

  useEffect(() => {
    loadAlerts();
    
    // Set up periodic refresh for real-time monitoring
    const interval = setInterval(loadAlerts, 300000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, [selectedLocation]);

  const loadAlerts = async () => {
    try {
      setIsLoading(true);
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7); // Last 7 days

      const trendsData = await nutritionApi.getNutritionalTrends({
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        location: selectedLocation
      });

      // Extract alerts from analytics data
      const currentAlerts = trendsData.analytics.alerts || [];
      
      // Add custom alerts based on recent data
      const customAlerts: Alert[] = [];
      
      if (trendsData.data.length > 0) {
        const latest = trendsData.data[trendsData.data.length - 1];
        
        // Protein deficiency alert
        if (latest.proteinPercentage < 12) {
          customAlerts.push({
            type: 'deficiency',
            nutrient: 'Protein',
            severity: latest.proteinPercentage < 10 ? 'high' : 'medium',
            message: `Protein level (${latest.proteinPercentage.toFixed(1)}%) is below minimum requirement`,
            location: selectedLocation,
            date: latest.date,
            value: latest.proteinPercentage,
            threshold: 12,
            recommendation: 'Increase high-protein feed components like pellets or seeds'
          });
        }

        // Fat excess alert
        if (latest.fatPercentage > 8) {
          customAlerts.push({
            type: 'excess',
            nutrient: 'Fat',
            severity: latest.fatPercentage > 12 ? 'high' : 'medium',
            message: `Fat level (${latest.fatPercentage.toFixed(1)}%) exceeds recommended maximum`,
            location: selectedLocation,
            date: latest.date,
            value: latest.fatPercentage,
            threshold: 8,
            recommendation: 'Reduce oily seeds and increase low-fat components'
          });
        }

        // Quality score alert
        if (latest.qualityScore < 70) {
          customAlerts.push({
            type: 'quality',
            nutrient: 'Overall Quality',
            severity: latest.qualityScore < 50 ? 'high' : latest.qualityScore < 60 ? 'medium' : 'low',
            message: `Nutritional quality score (${latest.qualityScore}/100) needs improvement`,
            location: selectedLocation,
            date: latest.date,
            value: latest.qualityScore,
            threshold: 75,
            recommendation: 'Review feed mix composition and balance macronutrients'
          });
        }
      }

      // Combine and format all alerts
      const allAlerts = [
        ...currentAlerts.map(alert => ({
          ...alert,
          date: new Date().toISOString().split('T')[0],
          location: selectedLocation
        })),
        ...customAlerts
      ];

      setAlerts(allAlerts);
    } catch (error) {
      console.error('Failed to load nutritional alerts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'high':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'medium':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'low':
        return <InformationCircleIcon className="h-5 w-5 text-blue-500" />;
      default:
        return <BellIcon className="h-5 w-5 text-slate-500" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'border-red-200 bg-red-50';
      case 'medium':
        return 'border-yellow-200 bg-yellow-50';
      case 'low':
        return 'border-blue-200 bg-cyan-50';
      default:
        return 'border-gray-200 bg-slate-50/80';
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    if (filter !== 'all' && alert.severity !== filter) return false;
    return true;
  });

  const highSeverityCount = alerts.filter(a => a.severity === 'high').length;
  const mediumSeverityCount = alerts.filter(a => a.severity === 'medium').length;
  const lowSeverityCount = alerts.filter(a => a.severity === 'low').length;

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <BellIcon className="h-6 w-6 text-cyan-600 mr-2" />
            <h3 className="text-lg font-medium text-slate-900">Nutritional Alerts</h3>
            {alerts.length > 0 && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-800">
                {alerts.length} active
              </span>
            )}
          </div>
          <button
            onClick={loadAlerts}
            disabled={isLoading}
            className="text-sm text-cyan-600 hover:text-cyan-500 disabled:opacity-50"
          >
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* Alert Summary */}
        <div className="mt-4 grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{highSeverityCount}</div>
            <div className="text-sm text-slate-500">High Priority</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">{mediumSeverityCount}</div>
            <div className="text-sm text-slate-500">Medium Priority</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-cyan-600">{lowSeverityCount}</div>
            <div className="text-sm text-slate-500">Low Priority</div>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-4 flex items-center space-x-4">
          <div className="flex space-x-1">
            {['all', 'high', 'medium', 'low'].map((severity) => (
              <button
                key={severity}
                onClick={() => setFilter(severity as any)}
                className={`px-3 py-1 text-xs font-medium rounded-full capitalize ${
                  filter === severity
                    ? 'bg-cyan-100 text-cyan-700'
                    : 'bg-gray-100 text-slate-700 hover:bg-gray-200'
                }`}
              >
                {severity}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-h-96 overflow-y-auto">
        {filteredAlerts.length === 0 ? (
          <div className="p-6 text-center">
            <CheckCircleIcon className="mx-auto h-12 w-12 text-green-400" />
            <h3 className="mt-2 text-sm font-medium text-slate-900">No Active Alerts</h3>
            <p className="mt-1 text-sm text-slate-500">
              All nutritional parameters are within acceptable ranges.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredAlerts.map((alert, index) => (
              <div
                key={index}
                className={`p-4 ${getSeverityColor(alert.severity)} border-l-4`}
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    {getSeverityIcon(alert.severity)}
                  </div>
                  <div className="ml-3 flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium text-slate-900">
                        {alert.nutrient} {alert.type}
                      </h4>
                      <span className="text-xs text-slate-500">{alert.date}</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-700">{alert.message}</p>
                    
                    {alert.value && alert.threshold && (
                      <div className="mt-2 text-xs text-slate-600">
                        Current: {alert.value.toFixed(1)}% | Target: {alert.threshold}%
                      </div>
                    )}
                    
                    {alert.location && (
                      <div className="mt-1 text-xs text-slate-500">
                        Location: {alert.location}
                      </div>
                    )}
                    
                    {alert.recommendation && (
                      <div className="mt-2 p-2 bg-white rounded border">
                        <p className="text-xs text-slate-700">
                          <strong>Recommendation:</strong> {alert.recommendation}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
} 